import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useMemo, useState, useEffect } from "react";
import { z } from "zod";
import { SlidersHorizontal, X, Search as SearchIcon } from "lucide-react";
import { StorefrontLayout } from "@/components/storefront/Layout";
import { ProductCard } from "@/components/storefront/ProductCard";
import { listingSearchSchema } from "@/lib/listing-search";
import type { ListingSearchParams } from "@/lib/listing-search";
import { useSearchProducts, type SearchProductRow } from "@/hooks/storefront/use-search-products";
import {
  normalizeSearch,
  tokenizeSearch,
  matchesTokens,
  productHaystack,
  scoreProduct,
} from "@/lib/search-utils";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { BrandLogo } from "@/components/storefront/BrandLogo";
import { useBrandLogos } from "@/hooks/storefront/use-brand-logos";
import { useSearchIndex } from "@/hooks/storefront/use-search-index";
import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { lookupUnavailableByQuery } from "@/lib/products.functions";
import { productSlug } from "@/lib/format";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";

const searchSchema = listingSearchSchema.extend({ q: z.string().optional().default("") });

export const Route = createFileRoute("/search")({
  validateSearch: searchSchema,
  head: () => ({ meta: [
      { property: "og:url", content: "https://mrpanda.gr/search" },
      { title: "Αναζήτηση — MrPanda.gr" },
      { name: "description", content: "Αναζήτησε ανάμεσα σε χιλιάδες προϊόντα στο MrPanda.gr και βρες αυτό που χρειάζεσαι στην καλύτερη τιμή." },
      { name: "robots", content: "noindex, follow" },
    ] }),
  component: SearchPage,
});

const RESULT_CAP = 500;
const GRID_CLASS =
  "grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 xl:grid-cols-8";

type SortKey = ListingSearchParams["sort"];

function sortList(arr: SearchProductRow[], sort: SortKey): SearchProductRow[] {
  const a = [...arr];
  if (sort === "price_asc") a.sort((x, y) => Number(x.price ?? 0) - Number(y.price ?? 0));
  else if (sort === "price_desc") a.sort((x, y) => Number(y.price ?? 0) - Number(x.price ?? 0));
  else if (sort === "alpha_asc") a.sort((x, y) => (x.name ?? "").localeCompare(y.name ?? "", "el"));
  else if (sort === "alpha_desc") a.sort((x, y) => (y.name ?? "").localeCompare(x.name ?? "", "el"));
  else if (sort === "best") a.sort((x, y) => Number(y.units_sold ?? 0) - Number(x.units_sold ?? 0));
  else if (sort === "old") a.sort((x, y) => new Date(x.date_add ?? 0).getTime() - new Date(y.date_add ?? 0).getTime());
  else a.sort((x, y) => new Date(y.date_add ?? 0).getTime() - new Date(x.date_add ?? 0).getTime());
  return a;
}

function SearchPage() {
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const { q, sort } = search;
  const { products, isReady, error } = useSearchProducts();
  const { run, isReady: indexReady } = useSearchIndex(true);

  const tokens = useMemo(() => tokenizeSearch(q ?? ""), [q]);
  const nq = useMemo(() => normalizeSearch(q ?? ""), [q]);

  const onChange = (next: Partial<typeof search>) =>
    navigate({ search: (prev: typeof search) => ({ ...prev, ...next }), replace: true });

  // token + price filter (brand facet is built from this, before brand filtering)
  const outcome = useMemo(
    () => (indexReady && nq ? run(q ?? "", 4000) : null),
    [indexReady, run, q, nq],
  );

  const prelim = useMemo(() => {
    if (!isReady) return [];
    let list = outcome
      ? outcome.items
      : nq
        ? products.filter((p) => matchesTokens(productHaystack(p), tokens))
        : products;
    if (search.priceMin != null) list = list.filter((p) => Number(p.price ?? 0) >= search.priceMin!);
    if (search.priceMax != null) list = list.filter((p) => Number(p.price ?? 0) <= search.priceMax!);
    return list;
  }, [products, isReady, tokens, nq, outcome, search.priceMin, search.priceMax]);

  const brandFacet = useMemo(() => {
    const m = new Map<number, { id: number; name: string; count: number }>();
    for (const p of prelim) {
      if (p.manufacturer_id == null) continue;
      const e = m.get(p.manufacturer_id);
      if (e) e.count++;
      else m.set(p.manufacturer_id, { id: p.manufacturer_id, name: p.manufacturer_name ?? "—", count: 1 });
    }
    return Array.from(m.values()).sort((a, b) => b.count - a.count);
  }, [prelim]);

  const filtered = useMemo(() => {
    if (!search.brands?.length) return prelim;
    const set = new Set(search.brands);
    return prelim.filter((p) => p.manufacturer_id != null && set.has(p.manufacturer_id));
  }, [prelim, search.brands]);

  const ranked = useMemo(() => {
    // Relevance ranking only when there is a query AND the user kept the default sort.
    if (nq && sort === "best") {
      // Το ευρετήριο επιστρέφει ήδη ταξινομημένα κατά συνάφεια αποτελέσματα.
      if (outcome) return filtered;
      return [...filtered].sort(
        (a, b) =>
          scoreProduct(b, tokens, nq) - scoreProduct(a, tokens, nq) ||
          Number(b.units_sold ?? 0) - Number(a.units_sold ?? 0),
      );
    }
    return sortList(filtered, sort);
  }, [filtered, nq, sort, tokens, outcome]);

  const capped = ranked.slice(0, RESULT_CAP);

  const activeFilters =
    (search.brands?.length ?? 0) +
    (search.priceMin != null ? 1 : 0) +
    (search.priceMax != null ? 1 : 0);

  return (
    <StorefrontLayout>
      <div className="mx-auto max-w-none px-4 pb-10">
        <div className="relative mb-6 mt-2 overflow-hidden rounded-3xl border border-border p-6 shadow-sm animate-pop-in sm:p-8 bg-gradient-to-br from-amber-200/60 via-accent/40 to-background">
          {/* floating background doodles */}
          {["🔎", "🛍️", "✨", "🐼", "💛"].map((d, i) => (
            <span
              key={i}
              aria-hidden
              className="pointer-events-none absolute select-none text-3xl opacity-20 animate-float-y sm:text-4xl"
              style={{
                top: `${12 + ((i * 37) % 64)}%`,
                left: `${68 + ((i * 17) % 28)}%`,
                animationDelay: `${i * 0.4}s`,
              }}
            >
              {d}
            </span>
          ))}
          <div className="relative flex items-center gap-4 sm:gap-5">
            <div className="grid h-16 w-16 shrink-0 place-items-center rounded-2xl bg-background/70 text-4xl shadow-sm backdrop-blur animate-bob hover-wiggle sm:h-20 sm:w-20 sm:text-5xl">
              🔍
            </div>
            <div className="min-w-0">
              <span className="inline-flex items-center gap-1.5 rounded-full bg-primary/15 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-primary">
                <SearchIcon className="h-3.5 w-3.5" />
                Αναζήτηση
              </span>
              <h1 className="mt-2 flex flex-wrap items-baseline gap-x-2 text-3xl font-extrabold tracking-tight text-foreground sm:text-4xl">
                {q ? (
                  <>
                    <span>Αποτελέσματα για</span>
                    <span className="text-primary">"{q}"</span>
                  </>
                ) : (
                  "Όλα τα προϊόντα"
                )}
              </h1>
              <p className="mt-1 text-sm text-muted-foreground sm:text-base">
                {!isReady
                  ? "Ο Mr Panda ψάχνει στη φωλιά… 🐼"
                  : ranked.length > 0
                    ? `Ο Mr Panda βρήκε ${ranked.length} ${ranked.length === 1 ? "προϊόν" : "προϊόντα"} για σένα! 🎉`
                    : "Ο Mr Panda έψαξε παντού… ας δοκιμάσουμε ξανά! 🧐"}
              </p>
            </div>
          </div>
        </div>

        <div className="mb-4 flex flex-wrap items-center justify-between gap-2 rounded-2xl border border-border bg-card/60 p-2 backdrop-blur sm:px-3">
          <div className="flex items-center gap-2">
            <Sheet>
              <SheetTrigger asChild>
                <Button variant="outline" size="sm" className="gap-2 rounded-full transition-transform hover:scale-105 active:scale-95">
                  <SlidersHorizontal className="h-4 w-4" />
                  Φίλτρα
                  {activeFilters > 0 && (
                    <span className="ml-1 animate-pop-in rounded-full bg-primary px-1.5 text-xs font-semibold text-primary-foreground">
                      {activeFilters}
                    </span>
                  )}
                </Button>
              </SheetTrigger>
              <SheetContent side="left" className="w-80 overflow-y-auto">
                <SheetHeader>
                  <SheetTitle>Φίλτρα</SheetTitle>
                </SheetHeader>
                <div className="mt-4">
                  <Filters search={search} brands={brandFacet} onChange={onChange} />
                </div>
              </SheetContent>
            </Sheet>
            <div className="hidden text-sm font-medium text-muted-foreground sm:block">
              {!isReady ? "Φόρτωση…" : ranked.length > 0 ? `${ranked.length} προϊόντα 🛍️` : "Κανένα προϊόν"}
            </div>
          </div>
          <select
            value={sort}
            onChange={(e) => onChange({ sort: e.target.value as typeof sort })}
            className="h-9 cursor-pointer rounded-full border border-input bg-background px-3 text-sm transition-colors hover:border-primary"
          >
            <option value="best">{q ? "Σχετικότερα" : "Περισσότερες πωλήσεις"}</option>
            <option value="new">Νεότερα</option>
            <option value="old">Παλαιότερα</option>
            <option value="price_asc">Τιμή: αύξουσα</option>
            <option value="price_desc">Τιμή: φθίνουσα</option>
            <option value="alpha_asc">Αλφαβητικά Α-Ω</option>
            <option value="alpha_desc">Αλφαβητικά Ω-Α</option>
          </select>
        </div>

        {error ? (
          <div className="rounded-lg border border-border bg-card p-8 text-center text-destructive">
            Σφάλμα: {error}
          </div>
        ) : !isReady ? (
          <div className={GRID_CLASS}>
            {Array.from({ length: 16 }).map((_, i) => (
              <div key={i} className="aspect-[3/4] animate-pulse rounded-lg border border-border bg-muted/40" />
            ))}
          </div>
        ) : capped.length === 0 ? (
          <NoResults q={q} />
        ) : (
          <>
            <div className={GRID_CLASS}>
              {capped.map((p) => (
                <ProductCard key={p.id} product={p as never} highlightTokens={tokens} />
              ))}
            </div>
            {ranked.length > RESULT_CAP && (
              <p className="mt-6 text-center text-sm text-muted-foreground">
                Εμφανίζονται τα πρώτα {RESULT_CAP} από {ranked.length} αποτελέσματα — φιλτράρετε για να δείτε λιγότερα.
              </p>
            )}
          </>
        )}
      </div>
    </StorefrontLayout>
  );
}

function NoResults({ q }: { q: string }) {
  const { data } = useQuery({
    queryKey: ["unavailable-lookup", q],
    queryFn: () => lookupUnavailableByQuery({ data: { q } }),
    enabled: q.trim().length >= 2,
    staleTime: 5 * 60_000,
  });
  const matches = data?.matches ?? [];
  return (
    <div className="animate-pop-in rounded-2xl border border-border p-8 text-center mp-playful-panel sm:p-12">
      <div className="mx-auto mb-4 grid h-24 w-24 place-items-center rounded-full bg-accent text-6xl animate-bob">🐼</div>
      <p className="mb-1 text-lg font-semibold">Χμμ, δεν βρήκαμε κάτι{q ? ` για "${q}"` : ""}!</p>
      <p className="text-sm text-muted-foreground">Ο πάντα έψαξε παντού... δοκίμασε άλλες λέξεις 🧐</p>
      {matches.length > 0 && (
        <div className="mx-auto mt-8 max-w-2xl text-left">
          <p className="mb-3 text-center text-sm font-semibold text-primary">
            😴 Βρήκαμε όμως {matches.length === 1 ? "ένα προϊόν που είναι" : "προϊόντα που είναι"} προσωρινά ανενεργά:
          </p>
          <ul className="space-y-2">
            {matches.map((m) => (
              <li key={m.id}>
                <Link
                  to="/p/$slug"
                  params={{ slug: productSlug(m.name, m.ps_id, m.id) }}
                  className="flex items-center gap-3 rounded-xl border border-border bg-card p-3 transition-colors hover:bg-accent"
                >
                  <div className="h-12 w-12 shrink-0 overflow-hidden rounded-lg bg-muted/40 p-1 grayscale">
                    {m.image_url ? (
                      <img src={m.image_url} alt={m.name} className="h-full w-full object-contain" loading="lazy" />
                    ) : (
                      <div className="grid h-full w-full place-items-center text-xl" aria-hidden>📦</div>
                    )}
                  </div>
                  <span className="min-w-0 flex-1 truncate text-sm font-medium">{m.name}</span>
                  <span className="shrink-0 rounded-full bg-muted px-2 py-0.5 text-[11px] font-semibold text-muted-foreground">
                    Ανενεργό 💤
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function Filters({
  search,
  brands,
  onChange,
}: {
  search: { priceMin?: number; priceMax?: number; brands?: number[] };
  brands: { id: number; name: string; count: number }[];
  onChange: (n: Partial<{ priceMin?: number; priceMax?: number; brands?: number[] }>) => void;
}) {
  const [minStr, setMinStr] = useState(search.priceMin?.toString() ?? "");
  const [maxStr, setMaxStr] = useState(search.priceMax?.toString() ?? "");
  useEffect(() => { setMinStr(search.priceMin?.toString() ?? ""); }, [search.priceMin]);
  useEffect(() => { setMaxStr(search.priceMax?.toString() ?? ""); }, [search.priceMax]);
  const brandLogos = useBrandLogos();

  const hasFilters = !!(search.priceMin || search.priceMax || search.brands?.length);
  const toggle = (arr: number[] | undefined, id: number): number[] => {
    const cur = arr ?? [];
    return cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id];
  };

  return (
    <aside className="space-y-5">
      {hasFilters && (
        <Button
          variant="ghost"
          size="sm"
          className="w-full justify-start gap-1"
          onClick={() => onChange({ priceMin: undefined, priceMax: undefined, brands: undefined })}
        >
          <X className="h-3 w-3" /> Καθαρισμός φίλτρων
        </Button>
      )}

      <div>
        <h3 className="mb-2 text-sm font-semibold">Τιμή (€)</h3>
        <div className="flex items-center gap-2">
          <Input type="number" min={0} placeholder="Από" value={minStr} onChange={(e) => setMinStr(e.target.value)} className="h-9" />
          <span className="text-muted-foreground">–</span>
          <Input type="number" min={0} placeholder="Έως" value={maxStr} onChange={(e) => setMaxStr(e.target.value)} className="h-9" />
        </div>
        <Button
          size="sm"
          variant="secondary"
          className="mt-2 w-full"
          onClick={() => onChange({ priceMin: minStr ? Number(minStr) : undefined, priceMax: maxStr ? Number(maxStr) : undefined })}
        >
          Εφαρμογή
        </Button>
      </div>

      {brands.length > 0 && (
        <div>
          <h3 className="mb-2 text-sm font-semibold">Μάρκα</h3>
          <ul className="max-h-72 space-y-1 overflow-auto pr-1">
            {brands.map((b) => (
              <li key={b.id}>
                <label className="flex cursor-pointer items-center justify-between gap-2 text-sm">
                  <span className="flex items-center gap-2 truncate">
                    <Checkbox
                      checked={search.brands?.includes(b.id) ?? false}
                      onCheckedChange={() => onChange({ brands: toggle(search.brands, b.id) })}
                    />
                    <BrandLogo
                      logoUrl={brandLogos.get(b.id)}
                      name={b.name}
                      className="h-7 w-7 shrink-0"
                      imgClassName="p-0.5"
                    />
                    <span className="truncate">{b.name}</span>
                  </span>
                  <span className="text-xs text-muted-foreground">{b.count}</span>
                </label>
              </li>
            ))}
          </ul>
        </div>
      )}
    </aside>
  );
}
